import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Table from 'reactstrap/lib/Table';
import { get, orderBy, isEmpty } from 'lodash-es';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { CmsEntities, bindToContent } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { EV_RANGE_LEADERBOARD_CONTENT_PATH } from 'client/site-modules/shared/constants/ev-range-tables';
import { Link } from 'site-modules/shared/components/link/link';
import { ShareThisChart } from 'client/site-modules/shared/components/share-this-chart/share-this-chart';
import edmundsLogo from 'client/images/edmunds-logo-226.png';

import './ev-range-table.scss';

export const SORT_ORDER = {
  NONE: 'none',
  ASCENDING: 'ascending',
  DESCENDING: 'descending',
};

const SORT = {
  [SORT_ORDER.ASCENDING]: 'asc',
  [SORT_ORDER.DESCENDING]: 'desc',
};

const SORT_COLUMNS = [
  {
    text: 'EPA estimate',
    sortKey: 'estimate',
  },
  {
    text: 'Edmunds tested',
    sortKey: 'tested',
  },
  {
    text: 'EPA estimate',
    sortKey: 'estimatedConsumption',
  },
  {
    text: 'Edmunds tested',
    sortKey: 'testedConsumption',
  },
  {
    text: 'Ambient temperature',
    sortKey: 'ambientTemperature',
  },
];

export function SortableCol({
  onClick,
  text,
  sortOrder = SORT_ORDER.NONE,
  sortKey,
  hasLeftBorder = false,
  hasCategoryBorder = false,
}) {
  return (
    <th
      scope="col"
      className={classnames('text-center align-middle bg-gray-lightest', {
        'border-start': hasLeftBorder,
        'category-border': hasCategoryBorder,
      })}
      aria-sort={sortOrder}
    >
      <button
        type="button"
        className="sort-btn text-gray-darker size-14 fw-bold p-0 d-flex align-items-center"
        onClick={onClick}
        data-sort-key={sortKey}
        data-testid={sortKey}
      >
        {text}
        <div className="arrows d-flex flex-column justify-content-center ms-0_25">
          {sortOrder !== SORT_ORDER.DESCENDING && <i className="arrow icon-arrow-up5" aria-hidden />}
          {sortOrder !== SORT_ORDER.ASCENDING && <i className="arrow icon-arrow-down5" aria-hidden />}
        </div>
      </button>
    </th>
  );
}

SortableCol.propTypes = {
  onClick: PropTypes.func.isRequired,
  text: PropTypes.string.isRequired,
  sortOrder: PropTypes.string,
  sortKey: PropTypes.string.isRequired,
  hasLeftBorder: PropTypes.bool,
  hasCategoryBorder: PropTypes.bool,
};

const getChangeTextClassNames = value => (value.startsWith('+') ? 'green' : 'red');

class EvRangeTableUI extends Component {
  static propTypes = {
    content: CmsEntities.Content,
    shareOptions: PropTypes.shape({
      canonical: PropTypes.string,
      pageTitle: PropTypes.string,
    }),
  };

  static defaultProps = {
    content: DEFAULT_CONTENT,
    shareOptions: {},
  };

  state = {
    sortedVehiclesData: [],
    activeSortKey: null,
    sortOrder: null,
  };

  componentDidMount() {
    this.setTableSizes();
  }

  static getDerivedStateFromProps(props, state) {
    if (isEmpty(state.sortedVehiclesData)) {
      const { content } = props;
      const vehiclesEntry = content.hasChild('vehicles') ? content.child('vehicles') : content.child('vehicles-1');
      const vehicles = vehiclesEntry.children().map(entry => entry.getAllMetadata());

      return {
        sortedVehiclesData: vehicles,
        activeSortKey: null,
        sortOrder: null,
      };
    }

    return null;
  }

  onSortChange = e => {
    const { sortOrder, sortedVehiclesData, activeSortKey } = this.state;

    e.stopPropagation();

    const sortKey = get(e, 'target.dataset.sortKey');

    const newSortOrder =
      sortKey !== activeSortKey || !sortOrder || sortOrder === SORT_ORDER.DESCENDING
        ? SORT_ORDER.ASCENDING
        : SORT_ORDER.DESCENDING;

    this.setState({
      sortedVehiclesData: orderBy(sortedVehiclesData, [sortKey], [SORT[newSortOrder]]),
      activeSortKey: sortKey,
      sortOrder: newSortOrder,
    });
  };

  setTableSizes() {
    this.setState({
      tableHeight: +get(this.ref, 'current.clientHeight', 0),
      tableWidth: +get(this.ref, 'current.clientWidth', 0),
    });
  }

  ref = React.createRef();

  render() {
    const { content, shareOptions } = this.props;

    if (!content || content.isDefault) return null;

    const { pageTitle, canonical } = shareOptions;
    const { sortedVehiclesData, activeSortKey, sortOrder, tableHeight, tableWidth } = this.state;

    const chartTitle = content.metadata('chartTitle').value();

    return (
      <div className="ev-range-table" ref={this.ref}>
        <ShareThisChart
          pageTitle={pageTitle}
          pageUrl={canonical}
          shareText="Share this table"
          shareableWidgetData={{
            backLink: canonical,
            defaultTitle: pageTitle,
            widgetName: 'tables',
            hostPageOverride: 'https://www.edmunds.com/widgets/tables/',
            queryParams: { contentId: EV_RANGE_LEADERBOARD_CONTENT_PATH },
            defaultWidth: tableWidth,
            defaultHeight: tableHeight,
          }}
          className="text-primary-darker text-end ms-auto"
        />
        <Table responsive className="text-gray-darker size-14">
          <caption className="visually-hidden">{chartTitle}</caption>
          <colgroup>
            <col width="17%" />
            <col width="17%" />
            <col width="17%" />
            <col width="17%" />
            <col width="17%" />
            <col width="15%" />
          </colgroup>
          <thead>
            <tr>
              <th colSpan={1} scope="colgroup" className="text-center align-middle">
                <img src={edmundsLogo} height={14} alt="Edmunds" className="mw-100" />
              </th>
              <th
                colSpan={2}
                scope="colgroup"
                className="text-center align-middle bg-light-blue size-16 category-border"
              >
                Range
              </th>
              <th
                colSpan={2}
                scope="colgroup"
                className="text-center align-middle bg-light-blue size-16 category-border"
              >
                Consumption
              </th>
              <th
                colSpan={1}
                scope="colgroup"
                className="text-center align-middle bg-light-blue size-16 category-border"
              >
                Conditions
              </th>
            </tr>
            <tr>
              <th scope="col" className="text-center align-middle bg-light-blue size-16">
                Vehicle
              </th>
              {SORT_COLUMNS.map((column, ind) => (
                <SortableCol
                  key={column.sortKey}
                  text={column.text}
                  sortKey={column.sortKey}
                  sortOrder={activeSortKey === column.sortKey ? sortOrder : SORT_ORDER.NONE}
                  onClick={this.onSortChange}
                  hasLeftBorder={ind % 2 === 1}
                  hasCategoryBorder={ind % 2 === 0}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedVehiclesData.map(vehicle => {
              const {
                chartVehicleName,
                tested,
                estimatedConsumption,
                testedConsumptionChange,
                testedConsumption,
                testedRangeChange,
                ambientTemperature,
                testedRangeDisclaimer,
                estimate,
                href,
              } = vehicle;

              return (
                <tr key={chartVehicleName}>
                  <th scope="row">
                    <Link to={href} className="text-primary-darker fw-normal">
                      {chartVehicleName}
                    </Link>
                  </th>
                  <td className="text-center align-top category-border">{estimate} miles</td>
                  <td className="text-center align-top border-start">
                    {tested} miles{testedRangeDisclaimer}
                    <br />
                    <span className={classnames('text-italic', getChangeTextClassNames(testedRangeChange))}>
                      ({testedRangeChange})
                    </span>
                  </td>
                  <td className="text-center align-top category-border">{estimatedConsumption}</td>
                  <td className="text-center align-top border-start">
                    {testedConsumption}
                    <br />
                    <span className={classnames('text-italic', getChangeTextClassNames(testedConsumptionChange))}>
                      ({testedConsumptionChange})
                    </span>
                  </td>
                  <td className="text-center align-top category-border">{ambientTemperature}&deg;</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  }
}

export const stateToPropsConfig = {
  content: bindToContent(EV_RANGE_LEADERBOARD_CONTENT_PATH),
};

export const EvRangeTable = connectToModel(EvRangeTableUI, stateToPropsConfig);
